import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { LazyRender } from '@packages/modules/src/LazyRender/LazyRender';
import dynamic from 'next/dynamic';
import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { registerFlexibleComponentsFooter } from '../../components/ComponentRecognizer/registerFlexibleComponentsFooter';
import type { CmsData } from '../../../interfaces/webapp_json';
import { AreaTop } from '../../components/Footer/AreaTop/AreaTop';
import { AreaSocket } from '../../components/Footer/AreaSocket/AreaSocket';
import { FooterFallbackLoading } from './FooterFallbackLoading';
import { FooterFallbackError } from './FooterFallbackError';

// register the dynamic components
registerFlexibleComponentsFooter();

type PageRendererProps = {
  data: CmsData;
  socketOnly?: boolean;
};

const DynamicBlockContent = dynamic(
  () =>
    import(
      /* webpackChunkName: 'CMS_FooterBlockContent' */ '../../components/Footer/BlockContent/BlockContent'
    ),
  {
    ssr: false,
  },
);

const DynamicFooterAccordion = dynamic(
  () => import(/* webpackChunkName: 'CMS_FooterAccordion' */ './FooterAccordion'),
  {
    ssr: false,
  },
);

export const PageRenderer = ({ data, socketOnly = false }: PageRendererProps) => {
  const { isMobile } = useDeviceType();

  return (
    <>
      {!socketOnly && <AreaTop data={data} />}
      <div
        // mt-11.5 doesn't exist, using the rem instead
        className={cn(
          'bg-color-grey-light text-color-text',
          socketOnly ? 'mt-[5.75rem]' : 'mt-[3.25rem]',
          'lg:mt-auto [&_p_a]:hover:underline!',
          'lg:[&.footer_block_badges]:flex lg:[&.footer_block_badges]:gap-3 lg:[&>p]:m-0 lg:[&>p+_p]:pt-1',
        )}
      >
        <ErrorBoundary fallback={<FooterFallbackError />}>
          <LazyRender fallback={<FooterFallbackLoading />}>
            {!socketOnly &&
              (isMobile ? (
                <DynamicFooterAccordion data={data} />
              ) : (
                <DynamicBlockContent data={data} />
              ))}
          </LazyRender>
        </ErrorBoundary>
        <AreaSocket data={data} />
      </div>
    </>
  );
};
